import { usePunditUserContext } from "@/react/contexts";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { LinkButton } from "@circle-react-uikit/LinkButton";
import { Typography } from "@circle-react-uikit/Typography";
import { useShowCoursesDirectory } from "./OptionsModal/hooks/useShowCoursesDirectory";

const TWO_LINES_TITLE_THRESHOLD = 20;

export const Banner = () => {
  const { data, isLoading } = useShowCoursesDirectory();
  const { currentCommunityMember } = usePunditUserContext();
  const isLoggedIn = !!currentCommunityMember;
  if (
    isLoading ||
    !data ||
    (isLoggedIn && !data.logged_in_banner_enabled) ||
    (!isLoggedIn && !data.logged_out_banner_enabled)
  ) {
    return null;
  }

  const bannerType = isLoggedIn ? "logged_in" : "logged_out";
  const bannerTitle = data[`${bannerType}_banner_title`];
  const bannerButtonLabel = data[`${bannerType}_banner_button_label`];
  const bannerText = data[`${bannerType}_banner_text`];
  const bannerButtonLink = data[`${bannerType}_banner_button_link`];
  const bannerImage = data[`${bannerType}_banner_image`];

  const isTwoLinesTitle =
    !!bannerTitle && bannerTitle.length > TWO_LINES_TITLE_THRESHOLD;
  const isOneLineTitle =
    !!bannerTitle && bannerTitle.length <= TWO_LINES_TITLE_THRESHOLD;
  const hasBannerButtonLabel = !!bannerButtonLabel;

  return (
    <div className="bg-primary mb-6 flex min-h-[340px] flex-col-reverse md:flex-row md:rounded-3xl">
      <div
        className={classNames(
          "border-primary flex flex-1 items-center justify-center border-b md:rounded-l-3xl md:border-y md:border-l",
          {
            "md:rounded-r-3xl md:border-r": !bannerImage,
          },
        )}
      >
        <div
          className={classNames(
            "flex max-w-md flex-col gap-2 p-6 md:px-12 md:py-0",
            {
              "items-center text-center": !bannerImage,
            },
          )}
        >
          <div className="line-clamp-5 flex flex-col gap-2">
            {bannerTitle && (
              <Typography.TitleXl>
                <span className="line-clamp-2">{bannerTitle}</span>
              </Typography.TitleXl>
            )}
            {bannerText && (
              <Typography.BodyMd>
                <span
                  className={classNames({
                    "line-clamp-3": isTwoLinesTitle && hasBannerButtonLabel,
                    "line-clamp-4": isOneLineTitle && hasBannerButtonLabel,
                    "line-clamp-5": isTwoLinesTitle && !hasBannerButtonLabel,
                    "line-clamp-7": isOneLineTitle && !hasBannerButtonLabel,
                    "line-clamp-8": !bannerTitle && hasBannerButtonLabel,
                    "line-clamp-9": !bannerTitle && !hasBannerButtonLabel,
                  })}
                >
                  {bannerText}
                </span>
              </Typography.BodyMd>
            )}
          </div>
          {bannerButtonLabel && bannerButtonLink && (
            <LinkButton
              href={bannerButtonLink}
              className="mt-4 w-fit !rounded-full !px-6"
              variant="primary"
              target="_blank"
              rel="noreferrer"
              extraLarge
            >
              {bannerButtonLabel}
            </LinkButton>
          )}
        </div>
      </div>
      {bannerImage && (
        <div className="flex-1">
          <img
            src={bannerImage.image_variants?.medium}
            loading="lazy"
            alt={bannerTitle || undefined}
            className="h-full w-full object-cover md:rounded-r-3xl"
          />
        </div>
      )}
    </div>
  );
};
